/** TWO name forms, deliberately distinct: `MFE_NAMES` carries the canonical kebab slug, `MFE_REGISTRY_NAMES` the `admin-<slug>` form -- a worker used both interchangeably with no compile-time link. */

import { MFE_BASE_PATH } from '@federa/const/auth-session';
import { STANDARD_HEADERS } from '@federa/const/http-header-names';

export const MFE_NAMES = {
	ACTIONS: 'actions',
	ANALYTICS: 'analytics',
	AUCTIONS: 'auctions',
	GROUPS: 'groups',
	CHATS: 'chats',
	CICD: 'cicd',
	CONNECT: 'connect',
	/** The Connect CONTROL SPA: the G4 staff controls over the whole fleet's lines -- the answering switch, the call
	 *  limits, the number pool and who answers for which group, and the user lookup -- mounted only by `apps/federated`.
	 *  The member desk (`CONNECT`) carries none of it. */
	CONNECT_CONTROL: 'connect-control',
	/**
	 * The Finance MFE, which owns that section END TO END -- UI, gated flows, and the Stripe/UserDO bindings. Every
	 * `apps/admin-federa` Finance route is ONLY an embed point: the page body ships inside `mfe/admin-finance`, and the
	 * route's `initialView` picks which of that MFE's four views renders.
	 *
	 * IT AUTHENTICATES EVERY DISPATCH ITSELF through its G4-gated `admin-finance-*` wrappers, so it does not lean on
	 * the admin host's gate -- which is what lets the same bundle serve a standalone finance domain later. Stated on
	 * the name every host shell passes, because four page shells restated both paragraphs until 2026-08-22.
	 */
	FINANCE: 'finance',
	/** The general-ledger SPA: the G4 read view of the whole book, mounted only by `apps/federated`. */
	GENERAL_LEDGER: 'general-ledger',
	KNOWLEDGEBASE: 'knowledgebase',
	MARKET_MAKER: 'market-maker',
	NAVIGATOR: 'navigator',
	PROFILE: 'profile',
	NOOR: 'noor',
	NOTIFICATIONS: 'notifications',
	PAYMENTS_PAYOUTS: 'payments-payouts',
	WISHLISTS: 'wishlists',
	OFFERS: 'offers',
	TRADE: 'trade',
	/**
	 * The Users MFE, which owns the admin Users section end to end -- UI, gated flows, and the same Stripe/UserDO
	 * bindings `FINANCE` carries. It exists because the ledger ACTIONS an operator needs while looking at a user
	 * (approve, cancel, prioritize, request correction, refund) are money writes: they resolve Stripe secrets from the
	 * Secrets Store, which `apps/admin-federa` cannot read (it deploys through opennextjs-cloudflare, whose credential
	 * fails with error 10021). Hosting the section here is what makes those actions available on the user profile at
	 * all, rather than only on the Finance ledger surface.
	 *
	 * It AUTHENTICATES EVERY DISPATCH ITSELF through G4-gated wrappers, exactly as `FINANCE` does, so it does not lean
	 * on the admin host's gate.
	 */
	USERS: 'users',
	TITLE_REPORT: 'title-report',
	EXPLORE: 'explore',
} as const satisfies Record<string, string>;

/** `admin-<slug>` form of each MFE name used by the service registry (`const/service-registry-entries.ts`), the `analytics` worker's KV cache namespace, and its error-handler service tag. */
export const MFE_REGISTRY_NAMES = {
	ACTIONS: 'admin-actions',
	ANALYTICS: 'admin-analytics',
	AUCTIONS: 'admin-auctions',
	FINANCE: 'admin-finance',
	MARKET_MAKER: 'admin-market-maker',
	USERS: 'admin-users',
} as const satisfies Record<string, `admin-${string}`>;

/**
 * The `admin-` head every {@link MFE_REGISTRY_NAMES} value carries, as the STRIP test -- the READ half of the compose
 * decision this leaf already owns on the write side, and the same token the `satisfies Record<string,
 * `admin-${string}`>` clause above pins into the type.
 *
 * HOMED HERE rather than at the readers, because it is one decision about a naming convention this leaf legislates:
 * two rule leaves had each declared it privately as ADMIN_DIR_PREFIX_RE -- `QUAL-MFE-FRONTEND-FOREIGN-DATA-PLANE-001`
 * recovering a service's own slug from its workspace dir, and `QUAL-MFE-NESTED-MOUNT-HOST-UNSERVABLE-001` doing the
 * same for the dirs `MFE_REGISTRY_NAMES` does not enumerate -- and `QUAL-NO-CROSS-TOOL-IMPORTS-001` forbids either of
 * them importing the other, so `const/` is the one home both can reach. A convention change would otherwise have had
 * to find the record here AND both private copies.
 */
export const ADMIN_SERVICE_DIR_PREFIX_RE = /^admin-/;

/** Compose an MFE/SPA worker's flow-dispatch API base. A `@runtime-derived-ssot-helper` and the ONLY composition site
 *  for the dispatch contract fixed in `.claude/skills/service-spinup/SKILL.md`: it joins the slug to `MFE_BASE_PATH` so a
 *  rename is never swept across three hand-typed grammars. Paired forbid-rule `QUAL-MFE-API-BASE-HAND-COMPOSED-001`. Returns the base WITHOUT a trailing slash. */
export function buildMfeApiBase<Slug extends string>(slug: Slug): `${typeof MFE_BASE_PATH}/${Slug}/api` {
	return `${MFE_BASE_PATH}/${slug}/api`;
}

/** Path segment marking a worker-internal (double-underscore) MFE api prefix; its leading underscores are stripped off the derived service name. */
const INTERNAL_PREFIX_MARKER = '__';

/** The API path segment every MFE prefix opens with, and therefore the one segment that names no service. */
const API_PATH_SEGMENT = 'api';

/** Leading underscore run on an internal segment. Hoisted rather than inline so the marker above and the strip cannot drift. */
const LEADING_UNDERSCORES_RE = /^_+/;

/**
 * The SERVICE a worker's api prefix names: the first segment that is neither `api` nor worker-internal, else the
 * internal one with its underscores stripped, else the prefix verbatim. Read by the api-MFE worker factory to label
 * its own catch funnel and telemetry.
 * @runtime-derived-ssot-helper (const-leaf-pinned): the read side of the same `MFE_BASE_PATH`-rooted prefix grammar
 * {@link buildMfeApiBase} composes, homed here so the composer and the parser of one grammar sit in one leaf rather
 * than one in `const/` and one inline in `tools/http/worker-factory.ts` (whose `max-lines` cap the inline copy also
 * breached).
 */
export function deriveMfeServiceName(apiPrefix: string): string {
	const segments = apiPrefix.split('/').filter((segment) => segment.length > 0 && segment !== API_PATH_SEGMENT);
	const named = segments.find((segment) => !segment.startsWith(INTERNAL_PREFIX_MARKER));
	if (named !== undefined) return named;
	const underscored = segments.find((segment) => segment.startsWith(INTERNAL_PREFIX_MARKER));
	return underscored === undefined ? apiPrefix : underscored.replace(LEADING_UNDERSCORES_RE, '');
}

/**
 * Filename the injectable MFE entry chunk is emitted -- and served -- under. Shared by the rollup output name and the URL a host shell dials, so the two grammars cannot name the bundle differently.
 *
 * The stable entry filename every MFE/SPA bundle emits and the host dials by slug; the `_headers` builder
 *  (`buildMfeAssetHeadersFile`, `utils/mfe/vite-bundle-output.ts`) names its cache row from it.
 */
export const MFE_INIT_BUNDLE_FILENAME = 'init.js';

/** `MFE_BASE_PATH` without its leading slash -- the BUILD-OUTPUT root, since rollup resolves filename templates relative to `build.outDir`. The `startsWith` guard stops a bare `.slice(1)` eating a character. */
const MFE_ASSET_ROOT = MFE_BASE_PATH.startsWith('/') ? MFE_BASE_PATH.slice(1) : MFE_BASE_PATH;

/** Build-side twin of `buildMfeApiBase`. Hand-typing that root leaves no compile-time link to `MFE_BASE_PATH`, the prefix the host middleware routes on, so a remount 404s every bundle. No slashes.
 *  @runtime-derived-ssot-helper: derives the prefix from the same-file `MFE_ASSET_ROOT`, itself derived from the
 *  `MFE_BASE_PATH` const SSOT, so the build-output root and the path the host middleware routes on stay one decision.
 *  Paired forbid-rule `QUAL-MFE-ASSET-PREFIX-HAND-COMPOSED-001` bans the hand-composed shape. */
export function buildMfeAssetPrefix(slug: string): string {
	return `${MFE_ASSET_ROOT}/${slug}`;
}

/** Compose an MFE/SPA injectable entry chunk's BUILD-OUTPUT name -- the rollup `entryFileNames` value for the `init` input, deliberately stable and hash-free because the host shell's script tag references it.
 *  @runtime-derived-ssot-helper: joins the same-file {@link buildMfeAssetPrefix} to the same-file
 *  `MFE_INIT_BUNDLE_FILENAME`, so the rollup output name and the URL the host shell dials cannot name the bundle
 *  differently. */
export function buildMfeInitAssetPath(slug: string): string {
	return `${buildMfeAssetPrefix(slug)}/${MFE_INIT_BUNDLE_FILENAME}`;
}

/** Compose the SERVED URL of an MFE/SPA injectable bundle. Consumer side of `buildMfeInitAssetPath`: both host shells inject this path and the host middleware proxies it, so this helper links the loader's URL to the bundle filename the vite config chose.
 *  @runtime-derived-ssot-helper: derives the served URL from the `MFE_BASE_PATH` const SSOT and the same-file
 *  `MFE_INIT_BUNDLE_FILENAME`, the same two sources {@link buildMfeInitAssetPath} builds the emitted file from.
 *  Const-homed for its `const/` consumers (`const/chats-viewer-routes.ts` and siblings), which cannot reach `utils/`. */
export function buildMfeInitPath(slug: string): string {
	return `${MFE_BASE_PATH}/${slug}/${MFE_INIT_BUNDLE_FILENAME}`;
}

/** Event-name head of the `mfe-<slug>-<verb>` DOM grammar, shared by every verb below. Split out so the composers join
 *  named parts rather than restating the grammar. Named for the GRAMMAR, not for `ready`: it stopped being the ready
 *  handshake's private property when `-close` joined it (2026-09-03). */
const MFE_EVENT_PREFIX = 'mfe-';
const MFE_READY_EVENT_SUFFIX = '-ready';

/**
 * Verb tail of the DISMISS half of the grammar: an MFE mounted as an OVERLAY by a host tells that host it wants to be
 * taken down. Distinct from `-ready` in direction and in consequence -- `-ready` is the mount handshake every bundle
 * fires and whose silence strands the host on a timeout, this one is a single surface asking to be closed.
 *
 * ONE dispatcher (`mfe/admin-auctions/frontend/init.tsx`, the auction-manager modal) and NO in-tree listener: nothing in
 * this repo mounts `MFE_NAMES.AUCTIONS` with the `data-property-id` that selects the modal branch, and that entry's
 * docstring reserves the branch for external mounts. The event is composed here rather than hand-typed BECAUSE that
 * question is open: composition is correct whether or not an out-of-tree listener exists, while deleting the dispatch is
 * correct only if one does not. Whether the modal branch should exist at all is operator-owned.
 */
const MFE_CLOSE_EVENT_SUFFIX = '-close';

/**
 * Compose the DOM event type an MFE/SPA bundle fires once it has published `window.__federaMFE_<slug>`, and the type a
 * host shell listens on. The OTHER half of the mount handshake `buildMfeInitPath` opens.
 *
 * Deliberately NOT routed through `globalKey`: the window KEY is brand-prefixed because it must be an identifier-ish
 * property, while this is a plain DOM type both host shells already listen on. The two grammars differ ON PURPOSE and
 * `components/common/__tests__/create-mfe-mount.test.tsx` pins the asymmetry.
 *
 * WHY IT HAS A COMPOSER AT ALL: drift here fails with NO error signal on either side. The bundle loads, publishes its
 * global and fires an event nobody listens for, so `script.onerror` never fires -- nothing failed -- and the host waits
 * out `MFE_BUNDLE_LOAD_TIMEOUT_MS` and renders its ERROR FALLBACK over a perfectly healthy bundle. Warm loads take the
 * `isLoaded()` branch and never touch the event, so the break presents as intermittent and cold-load-only.
 *
 * @runtime-derived-ssot-helper: joins the same-file head and tail consts, so the dispatch site and both listener sites
 * cannot spell the handshake differently. Paired forbid-rule `QUAL-MFE-READY-EVENT-HAND-COMPOSED-001`.
 */
export function buildMfeReadyEventName<Slug extends string>(
	slug: Slug,
): `${typeof MFE_EVENT_PREFIX}${Slug}${typeof MFE_READY_EVENT_SUFFIX}` {
	return `${MFE_EVENT_PREFIX}${slug}${MFE_READY_EVENT_SUFFIX}`;
}

/**
 * Compose the DOM event type an OVERLAY-mounted MFE fires to ask its host to dismiss it. Sibling of
 * {@link buildMfeReadyEventName} over the same `mfe-<slug>-<verb>` grammar and the same shared head const.
 *
 * A SEPARATE composer rather than a verb parameter on one: the two verbs have different consequence classes and
 * different forbid-rule coverage, and `buildMfeReadyEventName` is named verbatim in
 * `QUAL-MFE-READY-EVENT-HAND-COMPOSED-001`'s `excludeLinePatterns` -- folding it into a general
 * `buildMfeEventName(slug, verb)` would strip that token from every compliant call site and make the rule fire on all
 * of them. The head const is the fold that IS safe here, and it is the one taken.
 *
 * @runtime-derived-ssot-helper: joins the shared same-file head to the `-close` tail, so the dispatch site cannot spell
 * the slug or the grammar independently. Slug arm of the pairing is `QUAL-MFE-SLUG-LITERAL-SSOT-001`, whose
 * `COMPOSED_WINDOW_RE` was widened to `-(?:ready|close)` in the same change that added this.
 */
export function buildMfeCloseEventName<Slug extends string>(
	slug: Slug,
): `${typeof MFE_EVENT_PREFIX}${Slug}${typeof MFE_CLOSE_EVENT_SUFFIX}` {
	return `${MFE_EVENT_PREFIX}${slug}${MFE_CLOSE_EVENT_SUFFIX}`;
}

// BUNDLE FILES

/** The Workers static-assets header manifest every MFE/SPA bundle emits at its dist root: the vite plugin names the
 *  emitted asset with it (`tools/mfe/vite-config.ts`) and its body is `buildMfeAssetHeadersFile`
 *  (`utils/mfe/vite-bundle-output.ts`). The file name is the platform's own, so it is spelled once, here. */
export const ASSET_HEADERS_FILE = '_headers';

/**
 * The regex SOURCE for a route-SSOT const named for one of the fleet's MFE slugs (`CHATS_MFE_API_BASE`, `TITLE_REPORT_ROUTES`):
 * built from `Object.keys(MFE_NAMES)` at module load so a service added tomorrow is covered with no edit -- the keys ARE
 * the screaming-snake form of each slug. The raw-fetch rules each compiled this alternation for themselves.
 * @runtime-derived-ssot-helper (const-leaf-pinned): derives from the same-file {@link MFE_NAMES}.
 */
export const MFE_ROUTE_CONST_NAME_SOURCE = `\\b(?:${Object.keys(MFE_NAMES).join('|')})_[A-Z0-9_]*(?:ROUTES?|API_BASE|API_PREFIX)\\b`;

// MFE

/** The query parameter a VERSIONED MFE entry URL carries its version in: `init.js?v=<version>` (iss-9d956529). The
 *  version is the entry's own entity tag, so a new build is a new URL and the browser may keep each one forever. */
export const MFE_ENTRY_VERSION_PARAM = 'v';

/**
 * The URL a page dials an MFE entry at: under its version when the page knows it, else the bare stable entry
 * ({@link buildMfeInitPath}), which is exactly what every page dialled before versions existed.
 * @runtime-derived-ssot-helper: joins the same-file {@link buildMfeInitPath} and {@link MFE_ENTRY_VERSION_PARAM}, so the
 * versioned URL and the stable one cannot name the entry differently.
 */
export function buildMfeEntryUrl(slug: string, version: string | null): string {
	const path = buildMfeInitPath(slug);
	return version === null || version === '' ? path : `${path}?${MFE_ENTRY_VERSION_PARAM}=${encodeURIComponent(version)}`;
}

/** An entity tag's opaque value, weak (`W/"x"`) or strong (`"x"`) -- the version of the entry it validates -- or null
 *  when there is none, so no version is ever invented. One reading for the host that asks the MFE worker and for the
 *  middleware that checks a versioned request against the answer.
 *  @runtime-derived-ssot-helper: the ONE reading of an entry's version off its validator, shared by the reader that
 *  publishes versions and the middleware that checks them, so the two cannot disagree on what a version is. */
export function mfeEntryVersionOf(entityTag: string | null): string | null {
	if (entityTag === null) return null;
	const opaque = entityTag.replace(/^W\//, '').replace(/^"|"$/g, '');
	return opaque === '' ? null : opaque;
}

/** The version an ANSWER to a request for the entry carries: its entity tag's value when the answer succeeded, else
 *  null -- a refusal's validator names the refusal page, never the entry.
 *  @runtime-derived-ssot-helper: composes the same-file {@link mfeEntryVersionOf}, so the host's per-render read and a
 *  long-open tab's check (`useMfeEntryUpdate`) read one answer the same way. */
export function mfeEntryVersionOfResponse(response: Pick<Response, 'ok' | 'headers'>): string | null {
	return response.ok ? mfeEntryVersionOf(response.headers.get(STANDARD_HEADERS.ETAG)) : null;
}
